import React, { FC } from 'react';
import { Box, FormControl } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useTranslation } from 'react-i18next';
import { getDateFnsLocale, getDatePickerFormat } from 'utils/format';

/**
 * DD-3687: the order-date range, as a filter either end of which works alone.
 *
 * Deliberately not `components/shared/date-from-to-filter`, which this is
 * otherwise a copy of — same pickers, same labels, same layout, so the two
 * look identical on screen. The difference is when it reports:
 *
 *     // the shared one
 *     } else if (date && toDate) {
 *         onDateChange([date, toDate]);
 *     }
 *
 * It emits only once **both** ends are set, so a from-only or to-only range
 * silently does nothing. The archive's resolver builds `$gte` and `$lte`
 * independently, so "everything since 2019" is a perfectly good query there —
 * and on an archive spanning years it is the more useful half of the feature.
 *
 * Changing the shared component instead would change the order list and
 * transport admin, which is a wider decision than this card.
 */

type HistoricalDateRangeFilterProps = {
    dateRange: [Date | null, Date | null];
    onDateRangeChange: (range: [Date | null, Date | null]) => void;
};

const HistoricalDateRangeFilter: FC<HistoricalDateRangeFilterProps> = ({ dateRange, onDateRangeChange }) => {
    const { t: translate } = useTranslation(['orders']);
    const [fromDate, toDate] = dateRange;

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
            <Box display="flex">
                <FormControl fullWidth sx={{ marginRight: 1 }}>
                    <DatePicker
                        format={getDatePickerFormat()}
                        label={translate('orders:DATE_FROM')}
                        value={fromDate}
                        maxDate={toDate ?? undefined}
                        onChange={(date) => onDateRangeChange([date, toDate])}
                        slotProps={{ field: { clearable: true } }}
                        data-test="historical-orders.date-from"
                    />
                </FormControl>
                <FormControl fullWidth>
                    <DatePicker
                        format={getDatePickerFormat()}
                        label={translate('orders:DATE_TO')}
                        value={toDate}
                        minDate={fromDate ?? undefined}
                        onChange={(date) => onDateRangeChange([fromDate, date])}
                        slotProps={{ field: { clearable: true } }}
                        data-test="historical-orders.date-to"
                    />
                </FormControl>
            </Box>
        </LocalizationProvider>
    );
};

export default HistoricalDateRangeFilter;
